/* eslint-disable import/prefer-default-export */
import * as Sentry from "@sentry/react";
import {
  getEmployerApplicationCount,
  getEmployerInterviewCount,
  getEmployerJobStats,
  getPlacementCounts,
  getOfferCounts,
  getEmployerAvgTimeToHireLastYear,
  selectPipelineTimescale,
  selectSpendPerSectorTimescale,
  selectJobsPerSectorTimescale,
  selectTthTimelineTimescale,
  selectApplicationActionsTrackingTimescale,
  getCandidateSubmission,
  getPslData,
} from "./helpers/employerAnalyticsHelpers";
import applicationActionTrackingData from "./adapters/employerAnalyticsAdapters/applicationActionTrackingAdapter";
import Employer from "../apiResources/Employer";
import EmployerAnalytics from "../apiResources/EmployerAnalytics";
import LeagueTableAgency from "../apiResources/LeagueTableAgency";
import CurrentUser from "../apiResources/CurrentUser";
// import JobPerformance from "../apiResources/JobPerformance";
import JobHiringProcessTimeline from "../apiResources/JobHiringProcessTimeline";
import YearlyHhTimeToHire from "../apiResources/YearlyHhTimeToHire";
import {
  LOAD_EMPLOYER_ANALYTICS,
  LOAD_EMPLOYER_PIPELINE_WIDGET,
  LOAD_EMPLOYER_PIPELINE_WIDGET_ERROR,
  LOAD_EMPLOYER_TIME_TO_HIRE_WIDGET,
  LOAD_EMPLOYER_TIME_TO_HIRE_WIDGET_ERROR,
  LOAD_EMPLOYER_OFFER_TO_PLACEMENT_WIDGET,
  LOAD_EMPLOYER_OFFER_TO_PLACEMENT_WIDGET_ERROR,
  LOAD_AGENCY_PERFORMANCE,
  LOAD_JOB_PERFORMANCE,
  LOAD_JOB_PERFORMANCE_ERROR,
  LOAD_PSL_PERFORMANCE,
  LOAD_PSL_PERFORMANCE_ERROR,
  EMPLOYER_ANALYTICS_FILTER,
  RESET_PAGINATION_FLAG,
  RESET_DEFAULT_FILTERS,
  LOAD_EMPLOYER_ANALYTICS_ERROR,
  LOAD_AGENCY_PERFORMANCE_ERROR,
  SELECT_COMPANY_FILTER,
  RESET_COMPANY_FILTER,
  RESET_DATE_RANGE_FILTER,
  SELECT_DATE_RANGE_FILTER,
  IS_DATE_PICKER_ACTIVE,
  SET_PIPELINE_TIMESCALE,
  SET_SPEND_PER_SECTOR_TIMESCALE,
  SET_JOBS_PER_SECTOR_TIMESCALE,
  SET_TTH_TIMELINE_TIMESCALE,
  SET_AAT_TIMESCALE,
  HANDLE_PDF_OPTIONS,
  SELECT_ALL,
  UNSELECT_ALL,
  SELECT_TAB,
  LOAD_EMPLOYER_SPEND_PER_SECTOR_WIDGET,
  LOAD_EMPLOYER_SPEND_PER_SECTOR_WIDGET_ERROR,
  LOAD_EMPLOYER_TOTAL_JOBS_POSTED_WIDGET,
  LOAD_EMPLOYER_TOTAL_JOBS_POSTED_WIDGET_ERROR,
  LOAD_EMPLOYER_TOTAL_JOBS_PER_SECTOR_WIDGET,
  LOAD_EMPLOYER_TOTAL_JOBS_PER_SECTOR_WIDGET_ERROR,
  LOAD_EMPLOYER_TIME_TO_HIRE_TIMELINE_WIDGET,
  LOAD_EMPLOYER_TIME_TO_HIRE_TIMELINE_WIDGET_ERROR,
  LOAD_EMPLOYER_APPLICATION_ACTIONS_TRACKING_WIDGET,
  LOAD_EMPLOYER_APPLICATION_ACTIONS_TRACKING_WIDGET_ERROR,
  SET_CURRENTLY_SELECTED_COMPANY,
  SET_PIPELINE_WIDGET_LOADING,
  SET_TIME_TO_HIRE_WIDGET_LOADING,
  SET_OFFER_TO_PLACEMENT_WIDGET_LOADING,
  SET_SPEND_PER_SECTOR_WIDGET_LOADING,
  SET_TOTAL_JOBS_POSTED_WIDGET_LOADING,
  SET_JOBS_PER_SECTOR_WIDGET_LOADING,
  SET_TTH_TIMELINE_WIDGET_LOADING,
  SET_AAT_WIDGET_LOADING,
} from "./types";
import { selectableCompanies } from "./adapters/childCompanyFilterAdapters";

const today = new Date();
const oneDayInMS = 24 * 60 * 60 * 1000;
const oneYearAgo = new Date(today - 365 * oneDayInMS)
  .toISOString()
  .substr(0, 10);

export const setAllWidgetsLoading = () => async (dispatch) => {
  dispatch({ type: SET_PIPELINE_WIDGET_LOADING });
  dispatch({ type: SET_TIME_TO_HIRE_WIDGET_LOADING });
  dispatch({ type: SET_OFFER_TO_PLACEMENT_WIDGET_LOADING });
  dispatch({ type: SET_SPEND_PER_SECTOR_WIDGET_LOADING });
  dispatch({ type: SET_TOTAL_JOBS_POSTED_WIDGET_LOADING });
  dispatch({ type: SET_JOBS_PER_SECTOR_WIDGET_LOADING });
  dispatch({ type: SET_TTH_TIMELINE_WIDGET_LOADING });
  dispatch({ type: SET_AAT_WIDGET_LOADING });
};

export const loadEmployerPipelineWidget =
  (employerId = null) =>
  async (dispatch) => {
    // might be better to rewrite all these methods to take an obj
    // instead of individual params? then we can just provide
    // the individual param we want and default the rest
    const fnParams = [undefined, undefined, employerId];

    try {
      const employer = await Employer.includes(["children", "currency"]).find(
        employerId
      );
      const employerJobStats = await getEmployerJobStats(
        ...fnParams,
        employer.data.currency.id
      );
      const employerApplicationCount = await getEmployerApplicationCount(
        ...fnParams
      );
      const employerInterviewCount = await getEmployerInterviewCount(
        ...fnParams
      );
      const placementCounts = await getPlacementCounts(...fnParams);
      const offerCounts = await getOfferCounts(...fnParams);

      dispatch({
        type: LOAD_EMPLOYER_PIPELINE_WIDGET,
        payload: {
          positions:
            employerJobStats.meta.stats.total_number_of_positions.custom_stat,
          applications: employerApplicationCount.meta.stats.total.count,
          interviews: employerInterviewCount.meta.stats.total.count,
          offers: offerCounts.meta.stats.total.count,
          placements: placementCounts.meta.stats.total.count,
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_PIPELINE_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerTimeToHireWidget =
  (employerId = null) =>
  async (dispatch) => {
    try {
      const fnParams = [undefined, undefined, employerId];

      const employerAvgTimeToHire = await getEmployerAvgTimeToHireLastYear(
        ...fnParams
      );
      const hhTimeToHire = await YearlyHhTimeToHire.all();

      dispatch({
        type: LOAD_EMPLOYER_TIME_TO_HIRE_WIDGET,
        payload: {
          yourCompany:
            employerAvgTimeToHire.meta.stats.time_to_hire_last_year.custom_stat,
          hiringHub: hhTimeToHire.data[0].lastTwelveMonths,
          industryAvg: 44,
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_TIME_TO_HIRE_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerOfferToPlacementWidget =
  (employerId = null) =>
  async (dispatch) => {
    const fnParams = [undefined, undefined, employerId];
    try {
      const placementCounts = await getPlacementCounts(...fnParams);
      const offerCounts = await getOfferCounts(...fnParams);

      const offerToPlacementPercentage = Math.round(
        (placementCounts.meta.stats.total.count /
          offerCounts.meta.stats.total.count) *
          100
      );

      const hhOfferToPlacementPercentage = Math.round(
        (placementCounts.meta.stats.hiring_hub_placements_past_12_months
          .custom_stat /
          offerCounts.meta.stats.hiring_hub_offers_past_12_months.custom_stat) *
          100
      );

      dispatch({
        type: LOAD_EMPLOYER_OFFER_TO_PLACEMENT_WIDGET,
        payload: {
          yourCompany: offerToPlacementPercentage,
          hiringHub: hhOfferToPlacementPercentage,
          // industryAvg: 50,
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_OFFER_TO_PLACEMENT_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerSpendPerSectorWidget =
  (employerId = null) =>
  async (dispatch) => {
    const fnParams = [undefined, undefined, employerId];
    try {
      const employerJobStats = await getEmployerJobStats(...fnParams);
      const employerSpendPerSectorCount =
        employerJobStats.meta.stats.spend_per_sector.custom_stat;

      dispatch({
        type: LOAD_EMPLOYER_SPEND_PER_SECTOR_WIDGET,
        payload: {
          sectors: Object.keys(employerSpendPerSectorCount),
          values: Object.values(employerSpendPerSectorCount),
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_SPEND_PER_SECTOR_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerTotalJobsPostedWidget =
  (employerId = null) =>
  async (dispatch) => {
    try {
      // if there's an employerId filter value passed we use that in the
      // Employer.find query, if not we use currentUser company ID val
      const id = employerId || (await CurrentUser.first()).data.companyId;
      const employer = await Employer.find(id);

      // if employer is a root parent data jobsPostedPerMonthLastYear
      // will be data for all its children.
      const employerJobsPostedPerMonth =
        employer.data.jobsPostedPerMonthLastYear;
      const employerJobsPostedMonths = Object.keys(employerJobsPostedPerMonth);
      const employerJobsPostedCount = Object.values(employerJobsPostedPerMonth);

      const zeroValuesAdjustment = (array) => array.map((value) => value + 0.2);

      dispatch({
        type: LOAD_EMPLOYER_TOTAL_JOBS_POSTED_WIDGET,
        payload: {
          months: employerJobsPostedMonths,
          values: zeroValuesAdjustment(employerJobsPostedCount),
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_TOTAL_JOBS_POSTED_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerTotalJobsPerSectorWidget =
  (employerId = null) =>
  async (dispatch) => {
    try {
      const fnParams = [undefined, undefined, employerId];

      const employerJobStats = await getEmployerJobStats(...fnParams);
      const employerJobsPerSectorCount =
        employerJobStats.meta.stats.positions_per_sector.custom_stat;

      dispatch({
        type: LOAD_EMPLOYER_TOTAL_JOBS_PER_SECTOR_WIDGET,
        payload: {
          sectors: Object.keys(employerJobsPerSectorCount),
          values: Object.values(employerJobsPerSectorCount),
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_TOTAL_JOBS_PER_SECTOR_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerTimeToHireTimelineWidget =
  (employerId = null) =>
  async (dispatch) => {
    try {
      const fnParams = [undefined, undefined, employerId];

      const employerJobStats = await getEmployerJobStats(...fnParams);
      const employerTimeToHirePerMonthCount =
        employerJobStats.meta.stats.time_to_hire_per_month.custom_stat;

      const hhTimeToHire = await YearlyHhTimeToHire.all();

      dispatch({
        type: LOAD_EMPLOYER_TIME_TO_HIRE_TIMELINE_WIDGET,
        payload: {
          months: Object.keys(hhTimeToHire.data[0].lastTwelveMonthsMonthly),
          yourCompany: Object.values(employerTimeToHirePerMonthCount),
          hiringHub: Object.values(
            hhTimeToHire.data[0].lastTwelveMonthsMonthly
          ),
          industryAvg: [44, 44, 44, 44, 44, 44, 44, 44, 44, 44, 44, 44],
        },
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_TIME_TO_HIRE_TIMELINE_WIDGET_ERROR,
        payload: error,
      });
    }
  };

export const loadEmployerApplicationActionsTrackingWidget =
  (employerId = null) =>
  async (dispatch) => {
    try {
      const jobTimelines = await JobHiringProcessTimeline.where({
        job_posted_date: { gte: oneYearAgo },
        employer_id: employerId,
      }).all();

      dispatch({
        type: LOAD_EMPLOYER_APPLICATION_ACTIONS_TRACKING_WIDGET,
        payload: applicationActionTrackingData(jobTimelines.data),
      });
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_APPLICATION_ACTIONS_TRACKING_WIDGET_ERROR,
        payload: error,
      });
    }
  };

// employer analytics (Your Performance tab)
export const loadEmployerAnalytics = () => async (dispatch) => {
  try {
    const currentUser = await CurrentUser.first();
    const employer = await Employer.includes(["children", "currency"]).find(
      currentUser.data.companyId
    );

    dispatch({
      type: LOAD_EMPLOYER_ANALYTICS,
      payload: {
        isPremiumOrPortalUser: employer.data.premium || employer.data.portal,
        isPortalUser: employer.data.portal,
        isPremiumUser: employer.data.premium,
        isRootParentEmployer: currentUser.data.isRootParentEmployer,
        currencySymbol: employer.data.currency.symbol,
        companyData: {
          selectedCompany: {
            id: employer.data.id,
            name: employer.data.name,
          },
          currentCompany: employer.data,
          selectableCompanies: employer.data.children.map((child) => ({
            id: child.id,
            name: child.name,
          })),
        },
        companiesList: [
          { id: 1, name: "Stiedemann, Murphy and Lang" },
          { id: 2, name: "Stamm-Kilback" },
          { id: 3, name: "Carroll-Luettgen" },
          { id: 4, name: "Tillman and Sons" },
          { id: 5, name: "DuBuque Inc" },
        ],
      },
    });
  } catch (error) {
    dispatch({
      type: LOAD_EMPLOYER_ANALYTICS_ERROR,
      payload: error,
    });
  }
};

export const setCurrentlySelectedCompany =
  (company, currentUserCompany) => (dispatch) => {
    const companyData = {
      selectedCompany: company,
      currentUserCompany,
      selectableCompanies: selectableCompanies(currentUserCompany, company),
    };
    dispatch({
      type: SET_CURRENTLY_SELECTED_COMPANY,
      payload: companyData,
    });
  };

export const selectCompanyFilter = (company) => (dispatch) => {
  dispatch({
    type: SELECT_COMPANY_FILTER,
    payload: company,
  });
};

export const resetCompanyFilter = () => (dispatch) => {
  dispatch({
    type: RESET_COMPANY_FILTER,
  });
};

export const resetDateRangeFilter = () => (dispatch) => {
  dispatch({
    type: RESET_DATE_RANGE_FILTER,
  });
};

export const selectDateRangeFilter = (dates) => (dispatch) => {
  dispatch({
    type: SELECT_DATE_RANGE_FILTER,
    payload: dates,
  });
};

export const isDatePickerActive = (focusedInput) => (dispatch) => {
  dispatch({
    type: IS_DATE_PICKER_ACTIVE,
    payload: focusedInput,
  });
};

// timescale filters
export const selectWidgetTimescale =
  ({ type, timescale }) =>
  async (dispatch) => {
    dispatch({
      type: `SET_${type}_WIDGET_LOADING`,
    });
    try {
      if (type === "PIPELINE") {
        const pipelineData = await selectPipelineTimescale(timescale);
        dispatch({
          type: SET_PIPELINE_TIMESCALE,
          payload: pipelineData,
        });
      }
      if (type === "SPEND_PER_SECTOR") {
        const spendPerSectorData = await selectSpendPerSectorTimescale(
          timescale
        );
        dispatch({
          type: SET_SPEND_PER_SECTOR_TIMESCALE,
          payload: spendPerSectorData,
        });
      }
      if (type === "JOBS_PER_SECTOR") {
        const jobsPerSectorData = await selectJobsPerSectorTimescale(timescale);
        dispatch({
          type: SET_JOBS_PER_SECTOR_TIMESCALE,
          payload: jobsPerSectorData,
        });
      }
      if (type === "TTH_TIMELINE") {
        const timeToHireTimelineData = await selectTthTimelineTimescale(
          timescale
        );
        dispatch({
          type: SET_TTH_TIMELINE_TIMESCALE,
          payload: timeToHireTimelineData,
        });
      }
      if (type === "AAT") {
        const applicationActionsTrackingData =
          await selectApplicationActionsTrackingTimescale(timescale);
        dispatch({
          type: SET_AAT_TIMESCALE,
          payload: applicationActionsTrackingData,
        });
      }
    } catch (error) {
      dispatch({
        type: LOAD_EMPLOYER_ANALYTICS_ERROR,
        payload: error,
      });
    }
  };

// agency performance metrics (Agency Performance tab)
export const loadAgencyPerformance = () => async (dispatch) => {
  try {
    let placementSummary = {};
    let fillRate = {};
    let timeToSubmission = {};
    const currentUser = await CurrentUser.first();
    const employerId = parseInt(currentUser.data.companyId, 10);

    const {
      data: [employerAnalytics],
    } = await EmployerAnalytics.all();

    const leagueTableAgenciesScope = LeagueTableAgency.order({
      placement_count: "desc",
    })
      .order({ engagement_count: "desc" })
      .order({ interview_rate: "desc" })
      .per(20)
      .page(1);

    const employer = await Employer.includes({
      league_table: { league_table_agencies: { recruiter: "admin_users" } },
    })
      .merge({ league_table_agencies: leagueTableAgenciesScope })
      .find(employerId);

    const {
      data: { leagueTable },
    } = employer;
    const agencies = leagueTable ? leagueTable.leagueTableAgencies : [];

    placementSummary = {
      ...employerAnalytics.placementSummary,
    };

    fillRate = {
      ...employerAnalytics.fillRate,
    };

    timeToSubmission = {
      ...employerAnalytics.timeToSubmission,
    };

    dispatch({
      type: LOAD_AGENCY_PERFORMANCE,
      payload: {
        agencies,
        isPortalUser: employer.data.portal,
        isPremiumUser: employer.data.premium,
        placementSummary,
        fillRate,
        timeToSubmission,
      },
    });
  } catch (error) {
    Sentry.captureException(error);

    dispatch({
      type: LOAD_AGENCY_PERFORMANCE_ERROR,
      payload: error,
    });
  }
};

// NEW

export const analyticsFilter =
  (
    filters = {
      jobs: {},
      sectors: {},
      employer_users: {},
      timeframe: [],
    },
    filterAction = loadJobPerformance,
    dispatching = false,
    selectedFilters = {}
  ) =>
  (dispatch) => {
    if (dispatching) {
      dispatch(filterAction(selectedFilters));
    }
    dispatch({
      type: EMPLOYER_ANALYTICS_FILTER,
      payload: {
        filters,
        filterAction,
        dispatching,
        selectedFilters,
      },
    });
  };

export const loadJobPerformance =
  (filterData = {}) =>
  async (dispatch) => {
    const getFilters = filterData;
    try {
      let candidateSubmission = {};
      let rate = {};
      let placementRate = {};
      let placementMade = {};
      let stageProgression = {};
      let timeToSubmission = {};
      let fillRate = {};
      let jobSuccessFee = {};
      let costSpentOnPlacements = {};
      let recruitmentProcess = [];
      let timeToHire = {};
      let recruitmentProcessData = {};

      const jobPerformance = await getCandidateSubmission(getFilters);
      const jobPerformanceData = jobPerformance.data[0];

      recruitmentProcessData = jobPerformanceData.recruitmentProcess;
      recruitmentProcess = {
        categories: recruitmentProcessData.categories,
        min: recruitmentProcessData.min.map((number) => Math.round(number)),
        max: recruitmentProcessData.max.map((number) => Math.round(number)),
        mean: recruitmentProcessData.mean.map((number) => Math.round(number)),
      };

      // CANDIDATE SUBMISSION NUMBER
      candidateSubmission = jobPerformanceData.candidateSubmissionNumber;
      const filteredSectors = candidateSubmission.sectors.slice(0, 4);
      const filteredValues = candidateSubmission.values.slice(0, 4);
      candidateSubmission = {
        sectors: filteredSectors,
        values: filteredValues.map((number) => Math.round(number)),
      };
      // INTERVIEW & REJECTION RATE
      rate = jobPerformanceData.interviewAndRejectionRate;
      const convertedRateDates = rate.months.map(
        (dateString) => new Date(dateString)
      );
      rate = {
        months: convertedRateDates,
        interviewRates: rate.interviewRates.map((number) => Math.round(number)),
        rejectionRates: rate.rejectionRates.map((number) => Math.round(number)),
      };
      // PLACEMENTS RATE
      placementRate = jobPerformanceData.placementRate;
      const placementRateDates = placementRate.months.map(
        (dateString) => new Date(dateString)
      );
      placementRate = {
        months: placementRateDates,
        values: placementRate.values.map((number) => Math.round(number)),
      };
      // PLACEMENTS MADE
      placementMade = jobPerformanceData.placementsMade;
      const placementMadeDates = placementMade.months.map(
        (dateString) => new Date(dateString)
      );
      placementMade = {
        months: placementMadeDates,
        values: placementMade.values.map((number) => Math.round(number)),
      };
      // COST SPENT ON PLACEMENTS
      costSpentOnPlacements = jobPerformanceData.costOnPlacements;
      const costSpentOnPlacementsDates = costSpentOnPlacements.months.map(
        (dateString) => new Date(dateString)
      );
      costSpentOnPlacements = {
        months: costSpentOnPlacementsDates,
        values: costSpentOnPlacements.values.map((number) =>
          Math.round(number)
        ),
      };
      // TIME TO SUBMISSION
      timeToSubmission = jobPerformanceData.avgDaysToFirstSubmission;
      const timeToSubmissionDates = timeToSubmission.months.map(
        (dateString) => new Date(dateString)
      );
      timeToSubmission = {
        months: timeToSubmissionDates,
        values: timeToSubmission.values.map((number) => Math.round(number)),
      };
      // JOB SUCCESS FEE
      jobSuccessFee = jobPerformanceData.avgJobSuccessFee;
      const jobSuccessFeeDates = jobSuccessFee.months.map(
        (dateString) => new Date(dateString)
      );
      jobSuccessFee = {
        months: jobSuccessFeeDates,
        values: jobSuccessFee.values.map((number) => Math.round(number)),
      };
      // FILL RATE
      fillRate = jobPerformanceData.fillRate;
      const fillRateDates = fillRate.months.map(
        (dateString) => new Date(dateString)
      );
      fillRate = {
        months: fillRateDates,
        values: fillRate.filledRates.map((number) => Math.round(number)),
      };
      // TIME TO HIRE
      timeToHire = jobPerformanceData.avgTimeToHireDays;
      const convertedDates = timeToHire.months.map(
        (dateString) => new Date(dateString)
      );
      timeToHire = {
        months: convertedDates,
        values: timeToHire.values.map((number) => Math.round(number)),
      };
      // RECREUITMENT PROCESS
      // STAGE PROGRESSION
      stageProgression = jobPerformanceData.stageProgression;
      const jobs = jobPerformanceData.jobs;
      const employerUsers = jobPerformanceData.employerUsers;
      const sectors = jobPerformanceData.sectors;

      // const jobEntries = Object.fromEntries(Object.entries(jobs).slice(0, 10));
      // const employerEntries = Object.fromEntries(
      //   Object.entries(employerUsers).slice(0, 10)
      // );
      // const sectorsEntries = Object.fromEntries(
      //   Object.entries(sectors).slice(0, 10)
      // );

      const timeframe = [
        "Current Quarter",
        "This Month",
        "Last Month",
        "Last Quarter",
        "This Year",
        "Last 12 Months",
        "Last Year",
        "Custom Date",
      ];
      const filters = {
        jobs: jobs,
        employer_users: employerUsers,
        sectors: sectors,
        timeframe: timeframe,
      };

      dispatch(analyticsFilter(filters, loadJobPerformance, false, filterData));
      // CHARTS DATA ENDS HERE
      dispatch({
        type: LOAD_JOB_PERFORMANCE,
        payload: {
          filters,
          candidateSubmission,
          rate,
          placementRate,
          placementMade,
          stageProgression,
          jobSuccessFee,
          timeToHire,
          timeToSubmission,
          fillRate,
          costSpentOnPlacements,
          recruitmentProcess,
        },
      });
    } catch (error) {
      console.log(error.message);
      Sentry.captureException(error);

      dispatch({
        type: LOAD_JOB_PERFORMANCE_ERROR,
        payload: error,
      });
    }
  };
export const loadpslPerformance = (args) => async (dispatch) => {
  const finalArgs =
    args?.defaultFilters && Object.entries(args.defaultFilters).length > 0
      ? args.defaultFilters
      : args;
  const pagination = args?.pagination;
  // const filterData = args?.filterData;
  try {
    const responseWithArgs = await getPslData(finalArgs, pagination);
    const responseForFilters = await getPslData();
    const pslData = responseWithArgs.data;
    const sectorsData = {};
    const agencyNameData = {};

    responseForFilters.data.map((res) =>
      res.sectors.map((sector) => (sectorsData[sector.sector_id] = sector.name))
    );

    responseForFilters.data.map(
      (res) => (agencyNameData[res.id] = res.agencyName)
    );

    const timeframe = [
      "Current Quarter",
      "This Month",
      "Last Month",
      "Last Quarter",
      "This Year",
      "Last 12 Months",
      "Last Year",
      "Custom Date",
    ];
    const filters = {
      sortOrder: { asc: "Asc", desc: "Desc" },
      pslSectors: sectorsData,
      agencies: agencyNameData,
      sortBy: {
        agency_name: "Agency Name",
        // submission
        submissions_total_cand: "Total Candidates Submitted",
        submissions_time_to_first: "Days To First Submission",
        submissions_interview_rate: "Interview Rate",
        submissions_avg_per_role: "Average No. Submitted Per Role",
        // jobs
        jobs_total_worked: "Total number of Jobs Worked",
        jobs_placement_rate: "Placement Conversion Rate",
        jobs_last_placed: "Date Of Last Placement",
        jobs_total_filled: "Total Number Of Jobs Filled",
        // fees
        fees_total: "Total Fees",
        fees_avg_earnings: "Average Fee Earnings",
        fees_avg_rate: "Average Fee Rate",
      },
      timeframe: timeframe,
    };
    dispatch(analyticsFilter(filters, loadpslPerformance, false, finalArgs));
    // CHARTS DATA ENDS HERE
    dispatch({
      type: LOAD_PSL_PERFORMANCE,
      payload: {
        filters,
        pslData,
        totalCount: responseWithArgs.meta.stats.total.count.length,
        resetPagination: pagination ? false : true,
      },
    });
  } catch (error) {
    console.log(error.message);
    Sentry.captureException(error);

    dispatch({
      type: LOAD_PSL_PERFORMANCE_ERROR,
      payload: error,
    });
  }
};
export const resetPaginationFlag = () => ({
  type: RESET_PAGINATION_FLAG,
});

export const resetDefaultFilters = (args) => (dispatch) => {
  dispatch({
    type: RESET_DEFAULT_FILTERS,
    payload: args,
  });
};
// NEW ENDS
// select tab
export const selectTab = (tabType) => (dispatch) => {
  dispatch({
    type: SELECT_TAB,
    payload: tabType,
  });
};

// export to PDF
export const handlePdfExportOptions = (checkboxValue) => (dispatch) => {
  dispatch({
    type: HANDLE_PDF_OPTIONS,
    payload: checkboxValue,
  });
};

export const handleSelectAll = (userType) => (dispatch) => {
  dispatch({
    type: SELECT_ALL,
    payload: userType,
  });
};

export const handleUnselectAll = () => (dispatch) => {
  dispatch({
    type: UNSELECT_ALL,
  });
};
